<script lang="ts" setup>
import type { TabsItem } from '@nuxt/ui';
import Visualization from '~/app-modules/baseline/components/reviewTabs/visualization.vue';
import Statistics from '~/app-modules/baseline/components/reviewTabs/statistics.vue';
import ModelEquation from '~/app-modules/baseline/components/reviewTabs/modelEquation.vue';
import dayjs from 'dayjs';

// ================================
// Composables
// ================================
const { t } = useI18n();
const route = useRoute();
const { baseline } = storeToRefs(baselineStore());
const { getBaselinePrediction } = baselineRepository();

// ================================
// Data
// ================================
const activeTab = ref<string>('visualization');
const items = ref<TabsItem[]>([
  {
    label: t('baseline.visualization'),
    value: 'visualization',
  },
  {
    label: t('baseline.statistics'),
    value: 'statistics',
  },
  {
    label: t('baseline.model_equation'),
    value: 'model_equation',
  },
]);

onMounted(() => {
  if (baseline.value) {
    getBaselinePrediction({
      baselineId: Number(route.params.id),
      startDate: dayjs(baseline.value?.training_start).format('YYYY-MM-DD'),
      endDate: dayjs(baseline.value?.training_end).format('YYYY-MM-DD'),
    });
  }
});
</script>
<template>
  <div>
    <div class="w-full bg-white rounded-lg p-4 border border-gray-200">
      <!-- Title -->
      <h2 class="text-lg font-bold">
        {{ $t('baseline.review_title') }}
      </h2>
      <p class="text-sm text-gray-500">
        {{ $t('baseline.review_description') }}
      </p>

      <u-alert
        variant="subtle"
        :title="$t('baseline.review_iso_success_title')"
        :description="$t('baseline.review_iso_success_description')"
        class="my-4"
      />

      <!-- Tabs -->
      <u-tabs v-model:model-value="activeTab" :items="items" class="w-full">
        <template #content="{ item }">
          <visualization v-if="item.value === 'visualization'" />
          <statistics v-if="item.value === 'statistics'" />
          <model-equation v-if="item.value === 'model_equation'" />
        </template>
      </u-tabs>
    </div>

    <div class="w-full mt-8 flex flex-row justify-between">
      <u-button color="neutral" variant="outline" :label="$t('global.cancel')" @click="navigateTo('/references/baselines')" />
      <div class="flex items-center gap-2">
        <u-button
          color="neutral"
          variant="outline"
          :to="`/references/baselines/edit/${$route.params.id}/training-period`"
          :label="$t('global.previous')"
        />
        <u-button
          color="primary"
          variant="solid"
          :label="$t('global.next')"
          @click="navigateTo(`/references/baselines/edit/${$route.params.id}/validation`)"
        />
      </div>
    </div>
  </div>
</template>
